import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNowPlaying } from "../context/NowPlayingContext";
import { toast } from "sonner";
import { Play, Pause, Lock, Music as MusicIcon, Sparkles, Plus, ListMusic, Trash2 } from "lucide-react";

const CATEGORIES = ["All", "125 Hz", "250 Hz", "500 Hz", "1000 Hz"];

// ---------- Helpers ---------------------------------------------------------

function TierBadge({ tier }) {
  const t = (tier || "premium").toLowerCase();
  const isPlatinum = t === "platinum";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
      style={
        isPlatinum
          ? { background: "rgba(255,213,79,0.15)", border: "1px solid rgba(255,213,79,0.55)", color: "#FFD54F", boxShadow: "0 0 8px rgba(255,213,79,0.25)" }
          : { background: "rgba(0,150,136,0.18)", border: "1px solid rgba(0,150,136,0.55)", color: "#4DB6AC", boxShadow: "0 0 8px rgba(0,150,136,0.25)" }
      }
      data-testid={`tier-badge-${t}`}
    >
      {t.toUpperCase()}
    </span>
  );
}

function TrackRow({ track, isActive, isPreviewing, onPlay, onAdd }) {
  const showLock = track.access_level === "preview";
  const meta = track.category_meta || {};
  const songBenefits = Array.isArray(track.benefits) ? track.benefits : [];
  const catBenefits = Array.isArray(meta.benefits) ? meta.benefits : [];
  // Combine, de-dupe, cap at 4 chips so the card doesn't explode on long lists.
  const benefits = Array.from(new Set([...songBenefits, ...catBenefits])).slice(0, 4);
  const thumb = track.thumbnail_url || meta.thumbnail_url;

  return (
    <div
      className={`flex gap-4 p-4 rounded-2xl transition-all ${isActive ? "ring-1" : ""}`}
      style={{
        background: isActive ? "rgba(76,175,80,0.12)" : "rgba(8,14,10,0.78)",
        border: `1px solid ${isActive ? "rgba(76,175,80,0.55)" : "rgba(76,175,80,0.18)"}`,
        boxShadow: isActive ? "0 0 22px rgba(76,175,80,0.28)" : "0 4px 14px rgba(0,0,0,0.35)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      data-testid={`track-row-${track.id}`}
    >
      {/* Big thumbnail / category icon */}
      <button
        onClick={() => onPlay(track)}
        className="relative w-20 h-20 rounded-xl flex-shrink-0 overflow-hidden transition-all hover:scale-[1.03]"
        style={{
          background: thumb
            ? `url(${thumb}) center/cover no-repeat`
            : "linear-gradient(135deg, rgba(76,175,80,0.28), rgba(102,187,106,0.18))",
          border: `1px solid ${isActive ? "rgba(76,175,80,0.7)" : "rgba(76,175,80,0.4)"}`,
          boxShadow: isActive ? "0 0 18px rgba(76,175,80,0.45)" : "0 2px 10px rgba(0,0,0,0.35)",
        }}
        data-testid={`play-${track.id}`}
        aria-label={isActive ? `Pause ${track.title}` : `Play ${track.title}`}
      >
        {!thumb && (
          <span className="absolute inset-0 flex items-center justify-center" style={{ fontSize: 30 }}>
            {meta.icon || "🎵"}
          </span>
        )}
        <span
          className="absolute inset-0 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.42)" }}
        >
          {isActive
            ? <Pause size={22} className="text-white drop-shadow" />
            : <Play size={22} className="text-white ml-0.5 drop-shadow" />}
        </span>
      </button>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0 flex-1">
            <h4 className="font-bold text-[15px] text-white truncate leading-tight">{track.title}</h4>
            {track.artist && (
              <p className="text-[11px] text-muted-soft truncate mt-0.5">{track.artist}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <TierBadge tier={track.tier_required || track.subscription_tier} />
            {showLock && (
              <span title="30-second preview" className="inline-flex items-center" data-testid={`lock-${track.id}`}>
                <Lock size={11} className="text-[#FFD54F]" />
              </span>
            )}
          </div>
        </div>

        {/* Frequency + duration + category */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span
            className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
            style={{ background: "rgba(76,175,80,0.18)", color: "#A5D6A7", border: "1px solid rgba(76,175,80,0.38)" }}
          >
            {track.frequency}
          </span>
          <span className="text-[11px] text-muted-soft">{track.duration}</span>
          {meta.name && (
            <span className="text-[11px] text-muted-soft inline-flex items-center gap-1">
              <span>•</span>
              <span>{meta.icon ? `${meta.icon} ` : ""}{meta.name}</span>
            </span>
          )}
          {isPreviewing && <span className="text-[#FFD54F] text-[10px] font-bold ml-1">▸ Previewing</span>}
          {showLock && !isPreviewing && (
            <span className="text-[#FFD54F] text-[10px] font-semibold">30s preview</span>
          )}
        </div>

        {/* Benefits chips */}
        {benefits.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`benefits-${track.id}`}>
            {benefits.map((b) => (
              <span
                key={b}
                className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                style={{ background: "rgba(156,39,176,0.22)", color: "#E1BEE7", border: "1px solid rgba(212,155,255,0.4)" }}
              >
                {b}
              </span>
            ))}
          </div>
        )}

        {/* Category description (short) */}
        {meta.description && (
          <p className="text-[11px] text-soft mt-2 line-clamp-2 leading-snug">{meta.description}</p>
        )}
      </div>

      {onAdd && (
        <button
          onClick={() => onAdd(track)}
          className="self-start w-9 h-9 rounded-full inline-flex items-center justify-center hover:bg-white/5 flex-shrink-0"
          title="Add to playlist"
          data-testid={`add-${track.id}`}
        >
          <Plus size={16} className="text-[#d49bff]" />
        </button>
      )}
    </div>
  );
}

// ---------- Add-to-Playlist modal -------------------------------------------

function AddToPlaylistModal({ song, onClose, onAdded }) {
  const [playlists, setPlaylists] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/playlists").then((r) => setPlaylists(r.data?.playlists || [])).catch(() => {});
  }, []);

  const add = async (playlistId) => {
    setBusy(true);
    try {
      const r = await api.post(`/playlists/${playlistId}/songs`, { song_ids: [song.id] });
      if (r.data?.added === 0) toast.message("Already in playlist");
      else toast.success("Added to playlist");
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  };

  const createAndAdd = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/playlists", { name: newName.trim() });
      await api.post(`/playlists/${r.data.id}/songs`, { song_ids: [song.id] });
      toast.success(`Added to "${newName}"`);
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl p-5"
        style={{ background: "rgba(8,10,14,0.95)", border: "1px solid rgba(212,155,255,0.35)", maxHeight: "85vh", overflowY: "auto" }}
        data-testid="add-to-playlist-modal"
      >
        <h2 className="text-lg font-bold mb-1">Add to playlist</h2>
        <p className="text-xs text-muted-soft mb-4 truncate">{song.title}</p>

        {playlists.length > 0 && (
          <>
            <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-2">Your playlists</p>
            <div className="space-y-1.5 mb-5">
              {playlists.map((p) => (
                <button
                  key={p.id}
                  disabled={busy}
                  onClick={() => add(p.id)}
                  className="w-full px-3 py-3 rounded-xl flex items-center gap-3 text-left hover:bg-white/5 transition-colors"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                  data-testid={`add-to-${p.id}`}
                >
                  <ListMusic size={16} className="text-[#d49bff]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{p.name}</p>
                    <p className="text-[10px] text-muted-soft">{p.song_count} song{p.song_count === 1 ? "" : "s"}</p>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-2">Or create new</p>
        <div className="flex gap-2">
          <input
            className="glass-input flex-1"
            placeholder="Playlist name"
            maxLength={50}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="new-playlist-input"
          />
          <button
            onClick={createAndAdd}
            disabled={!newName.trim() || busy}
            className="px-4 rounded-xl font-bold text-sm disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff" }}
            data-testid="new-playlist-create"
          >
            Create
          </button>
        </div>

        <button onClick={onClose} className="mt-5 w-full btn-outline" data-testid="modal-close">Close</button>
      </div>
    </div>
  );
}

// ---------- Main screen ------------------------------------------------------

export default function MusicScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { track: npTrack, isPlaying, previewLeft, play: playTrack, stop: stopPlay } = useNowPlaying();
  const userTier = (user?.tier || "free").toLowerCase();
  const isPlatinum = userTier === "platinum";

  const [tab, setTab] = useState("songs"); // songs | playlists
  const [tracks, setTracks] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [cat, setCat] = useState("All");
  const [addSong, setAddSong] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/music/tracks");
        setTracks(data.tracks || []);
      } catch (e) { console.error(e); }
    })();
  }, [user?.tier]);

  const loadPlaylists = async () => {
    if (!isPlatinum) return;
    try {
      const { data } = await api.get("/playlists");
      setPlaylists(data.playlists || []);
    } catch (e) { /* 403 etc — silently ignore */ }
  };
  useEffect(() => { if (tab === "playlists") loadPlaylists(); }, [tab, isPlatinum]);

  const filtered = useMemo(
    () => tracks.filter((t) => cat === "All" || t.category === cat),
    [tracks, cat]
  );

  const activeId = npTrack?.id;

  // ----- Playlists tab -----

  const deletePlaylist = async (pl) => {
    if (!window.confirm(`Delete "${pl.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/playlists/${pl.id}`); toast.success("Deleted"); loadPlaylists(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Shell section="music">
      <header className="mb-5 fade-up">
        <h1 className="text-3xl font-extrabold tracking-tight text-shadow-green">Plant Frequency Music</h1>
        <p className="text-muted-soft text-sm mt-1">
          Healing frequencies tuned to specific growth phases.
        </p>
      </header>

      {/* Tabs */}
      <div
        className="inline-flex p-1 rounded-xl mb-5"
        style={{ background: "rgba(10,12,22,0.78)", border: "1px solid rgba(76,175,80,0.25)" }}
        data-testid="music-tabs"
      >
        <button
          onClick={() => setTab("songs")}
          className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
          style={
            tab === "songs"
              ? { background: "linear-gradient(135deg,#4CAF50,#66BB6A)", color: "#0a0a0a", boxShadow: "0 2px 14px rgba(76,175,80,0.45)" }
              : { color: "rgba(255,255,255,0.65)" }
          }
          data-testid="tab-songs"
        >
          Songs <span className="opacity-70 ml-1">({tracks.length})</span>
        </button>
        <button
          onClick={() => setTab("playlists")}
          className="px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1.5"
          style={
            tab === "playlists"
              ? { background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff", boxShadow: "0 2px 14px rgba(156,39,176,0.45)" }
              : { color: "rgba(255,255,255,0.65)" }
          }
          data-testid="tab-playlists"
        >
          Playlists
          {!isPlatinum && <Lock size={11} className="text-[#FFD54F]" />}
        </button>
      </div>

      {tab === "songs" && (
        <>
          {/* User-tier banner for non-Platinum */}
          {!isPlatinum && (
            <div
              className="rounded-2xl p-4 mb-5 flex items-center gap-3 fade-up"
              style={{ background: "rgba(255,213,79,0.10)", border: "1px solid rgba(255,213,79,0.35)" }}
              data-testid="tier-banner"
            >
              <Sparkles size={18} className="text-[#FFD54F] flex-shrink-0" />
              <div className="flex-1 text-sm">
                {userTier === "free" && (
                  <>You're on <b>Free</b> — every song plays as a <b>30-second preview</b>. Upgrade to listen in full.</>
                )}
                {userTier === "premium" && (
                  <>You have full access to Premium songs. <b>Platinum</b> songs play as a 30-second preview.</>
                )}
              </div>
              <button
                onClick={() => navigate("/subscription")}
                className="btn-amber text-xs px-3 py-1.5 whitespace-nowrap"
                data-testid="tier-banner-upgrade"
              >
                Upgrade
              </button>
            </div>
          )}

          {/* Category filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1" data-testid="category-filter">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className="px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
                style={
                  cat === c
                    ? { background: "linear-gradient(135deg,#4CAF50,#66BB6A)", color: "#0a0a0a", border: "1px solid rgba(76,175,80,0.7)" }
                    : { background: "rgba(8,14,10,0.55)", color: "rgba(255,255,255,0.75)", border: "1px solid rgba(255,255,255,0.08)" }
                }
                data-testid={`cat-${c}`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Track list */}
          <div className="space-y-3" data-testid="track-list">
            {filtered.length === 0 ? (
              <div className="glass-card-strong p-8 text-center">
                <MusicIcon size={28} className="mx-auto text-muted-soft mb-3" />
                <p className="text-soft text-sm">No tracks match this filter.</p>
              </div>
            ) : (
              filtered.map((t) => (
                <TrackRow
                  key={t.id}
                  track={t}
                  isActive={activeId === t.id && isPlaying}
                  isPreviewing={activeId === t.id && previewLeft != null && previewLeft > 0}
                  onPlay={playTrack}
                  onAdd={isPlatinum ? (s) => setAddSong(s) : null}
                />
              ))
            )}
          </div>
        </>
      )}

      {tab === "playlists" && (
        <>
          {!isPlatinum ? (
            <div
              className="rounded-2xl p-7 text-center fade-up"
              style={{ background: "rgba(8,14,10,0.72)", border: "1px solid rgba(255,213,79,0.45)", boxShadow: "0 0 24px rgba(255,213,79,0.18)" }}
              data-testid="playlists-locked"
            >
              <Lock size={32} className="mx-auto text-[#FFD54F] mb-3" />
              <h3 className="text-xl font-bold mb-1">Playlists are a Platinum feature</h3>
              <p className="text-soft text-sm mb-5">
                Curate your own collections of frequency music, reorder them, and play them on loop.
              </p>
              <button
                onClick={() => navigate("/subscription")}
                className="btn-amber inline-flex items-center gap-2"
                data-testid="playlists-upgrade"
              >
                <Sparkles size={14} /> Upgrade to Platinum
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-[11px] uppercase tracking-wider text-muted-soft">
                  {playlists.length} playlist{playlists.length === 1 ? "" : "s"}
                </p>
                <button
                  onClick={() => navigate("/music/playlists/new")}
                  className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl"
                  style={{ background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff", boxShadow: "0 4px 14px rgba(156,39,176,0.4)" }}
                  data-testid="create-playlist-btn"
                >
                  <Plus size={14} /> Create playlist
                </button>
              </div>

              {playlists.length === 0 ? (
                <div className="glass-card-strong p-10 text-center fade-up">
                  <ListMusic size={32} className="mx-auto text-[#d49bff] mb-3" />
                  <p className="text-soft text-sm">No playlists yet. Tap <b>Create playlist</b> to start.</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="playlist-list">
                  {playlists.map((p) => (
                    <div
                      key={p.id}
                      className="rounded-xl p-4 flex items-center gap-3 transition-all hover:bg-white/3"
                      style={{ background: "rgba(8,14,10,0.55)", border: "1px solid rgba(212,155,255,0.18)" }}
                      data-testid={`playlist-card-${p.id}`}
                    >
                      <button
                        onClick={() => navigate(`/music/playlists/${p.id}`)}
                        className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                        style={{ background: "rgba(123,31,162,0.25)", border: "1px solid rgba(212,155,255,0.35)" }}
                      >
                        {p.thumbnail_url ? (
                          <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ListMusic size={20} className="text-[#d49bff]" />
                        )}
                      </button>
                      <button onClick={() => navigate(`/music/playlists/${p.id}`)} className="flex-1 text-left min-w-0">
                        <p className="font-semibold truncate">{p.name}</p>
                        <p className="text-[11px] text-muted-soft">{p.song_count} song{p.song_count === 1 ? "" : "s"}</p>
                      </button>
                      <button
                        onClick={() => deletePlaylist(p)}
                        className="w-9 h-9 rounded-full hover:bg-red-500/10 inline-flex items-center justify-center"
                        title="Delete playlist"
                        data-testid={`delete-${p.id}`}
                      >
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {addSong && (
        <AddToPlaylistModal
          song={addSong}
          onClose={() => setAddSong(null)}
          onAdded={() => { setAddSong(null); loadPlaylists(); }}
        />
      )}
    </Shell>
  );
}
